/**
 * Helper functions for formatting and displaying center information.
 */

/**
 * Formats distance text from a center object.
 * @param {Object} center - Center object with distance property
 * @returns {string} Formatted distance text (e.g., "<1 mi", "2.5 mi", "10 mi")
 */
export function formatDistanceText(center) {
	let distanceText = "";
	if (center && center.distance !== undefined && center.distance !== null) {
		let distance = parseFloat(center.distance);
		if (!isNaN(distance)) {
			if (distance === 0) {
				distanceText = "<1 mi";
			} else if (distance < 1) {
				distanceText = distance.toFixed(1) + " mi";
			} else {
				distanceText = Math.round(distance) + " mi";
			}
		}
	}
	return distanceText;
}

/**
 * Gets the full brand name from a center object.
 * @param {Object} center - Center object with brand property
 * @returns {string} Full brand name
 */
export function getBrandName(center) {
	if (!center) {
		return 'LCA Vision';
	}
	if (center.brand === 'lvi') {
		return 'The Lasik Vision Institute';
	} else if (center.brand === 'lp') {
		return 'Lasik<em>Plus</em>';
	} else if (center.brand === 'tlc') {
		return 'TLC Laser Eye Centers';
	} else if (center.brand === 'wht') {
		return 'Whiting Clinic';
	} else if (center.brand === 'ldc') {
		return 'LASIK.com';
	} else {
		return center.brand || 'Vision Center';
	}
}

/**
 * Gets the brand image HTML for a center.
 * @param {Object} center - Center object with brand property
 * @param {string} pluginUrl - Base URL for the plugin (for image paths)
 * @returns {string} HTML string for brand image
 */
export function getBrandImageHTML(center, pluginUrl) {
	let brandImageHTML = '';
	if (!center) {
		return `<div class="center_brand_text">Vision Center</div>`;
	}
	if (center.brand === 'lvi') {
		brandImageHTML = `<img src="${pluginUrl}public/images/lvi-icon.svg" alt="Lasik Vision Institute">`;
	} else if (center.brand === 'lp') {
		brandImageHTML = `<img src="${pluginUrl}public/images/lasikplus-icon.svg" alt="LasikPlus">`;
	} else if (center.brand === 'tlc') {
		brandImageHTML = `<img src="${pluginUrl}public/images/tlc-icon.svg" alt="TLC Vision">`;
	} else if (center.brand === 'wht') {
		brandImageHTML = `<img src="${pluginUrl}public/images/whiting-logo.svg" alt="Whiting Clinic">`;
	} else {
		// Fallback for unknown brands or no brand
		brandImageHTML = `<div class="center_brand_text">${getBrandName(center)}</div>`;
	}
	return brandImageHTML;
}

/**
 * Creates HTML for a center list item.
 * @param {Object} center - Center object
 * @param {string} pluginUrl - Base URL for the plugin (for image paths)
 * @param {boolean} isAffiliate - Whether this is an affiliate center
 * @param {boolean} excludeButton - Whether to exclude the select button
 * @param {boolean} excludeBrandName - Whether to exclude the brand name from display
 * @param {boolean} excludeBrandImage - Whether to exclude the brand image from display
 * @returns {string} HTML string for center list item
 */
export function createCenterHTML(center, pluginUrl, isAffiliate = false, excludeButton = false, excludeBrandName = false, excludeBrandImage = false) {
	// Handle undefined or null center
	if (!center) {
		return '';
	}
	
	const distanceText = formatDistanceText(center);
	const brandImageHTML = excludeBrandImage ? '' : getBrandImageHTML(center, pluginUrl);
	const affiliateClass = isAffiliate ? ' isched_centers__list__item--affiliate' : '';
	
	// Build data attributes conditionally
	const dataAttrs = [];
	if (center.centerCode) {
		dataAttrs.push(`data-center-code="${center.centerCode}"`);
	}
	if (center.lat !== undefined && center.lat !== null) {
		dataAttrs.push(`data-lat="${center.lat}"`);
	}
	if (center.lon !== undefined && center.lon !== null) {
		dataAttrs.push(`data-lon="${center.lon}"`);
	}
	const dataAttrsStr = dataAttrs.length > 0 ? ' ' + dataAttrs.join(' ') : '';
	
	const buttonHTML = excludeButton ? '' : `
			<div class="isched_centers__list__item__button">
				<button class="isched-button isched-button--primary center-select-btn" data-center-code="${center.centerCode || ''}">Select Center<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M422.6 278.6L445.3 256l-22.6-22.6-144-144L256 66.7 210.8 112l22.6 22.6L322.8 224 32 224 0 224l0 64 32 0 290.7 0-89.4 89.4L210.8 400 256 445.3l22.6-22.6 144-144z"/></svg></button>
			</div>`;
	
	// Only show distance if we have distance text
	const distanceHTML = distanceText ? `<div class="isched_centers__list__item__distance">${distanceText}</div>` : '';
	const brandNameHTML = excludeBrandName ? '' : `<span class="isched_centers__list__item__name__brand_name">${getBrandName(center)} - </span>`;
	
	// Safely access center properties with fallbacks
	const city = center.city || '';
	const state = center.state || '';
	const address1 = center.address1 || '';
	const zipPostal = center.zipPostal || '';
	
	return `
		<li class="isched_centers__list__item${affiliateClass}"${dataAttrsStr}>
			<div class="isched_centers__list__item__wrapper">
				<div class="isched_centers__list__item__image">
					${brandImageHTML}
				</div>
				<div class="isched_centers__list__item__content">
					<div class="isched_centers__list__item__name"><div class="isched_centers__list__item__name__name">${center.brand === 'wht' ? 'Whiting Clinic' : `${brandNameHTML}${city}${state ? `<span class="isched_centers__list__item__name__state">, ${state}</span>` : ''} <span class="isched_centers__list__item__name__vision_center">Vision Center</span>`}</div>${distanceHTML}</div>
					<div class="isched_centers__list__item__address">${address1}${city && address1 ? ', ' : ''}${city}${state ? `, ${state}` : ''} ${zipPostal}</div>
					<div class="isched_centers__list__item__rating">
						${center.ratings ? `
						<div class="isched_star_rating">
							<div class="isched_star_rating__rating">${center.ratings}</div>
							<div class="isched_star_rating__stars" role="img" aria-label="${center.ratings} star rating"></div>
							${center.userRatingsTotal ? `
							<div class="isched_star_rating__reviews_count">${center.userRatingsTotal} Reviews</div>
							` : ''}
						</div>
						` : ''}
					</div>
				</div>
			</div>
			${buttonHTML}
		</li>
	`;
}
